import CampaignIcon from 'assets/icons/CampaignIcon'
import DocIcon from 'assets/icons/DocIcon'
import CloseButton from 'Components/Common/CloseButton'
import FieldInstruction from 'Components/Common/FieldInstruction'
import RichTextEditor from 'Components/Common/RichTextEditor'
import { FIELDS_INSTRUCTIONS } from 'constants/fieldsInstructions'
import React, { useEffect, useRef } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { Col, FormFeedback, FormGroup, Input, Label, Offcanvas, OffcanvasBody } from 'reactstrap'
import { createOfflineCampaign, searchCampaigns } from 'services/api/campaign'
import { AppDispatch, RootState } from 'store'
import { TrackedPost } from 'types/social-account'
import * as Yup from "yup";
import { useFormik } from "formik";
import { usePrivileges } from 'hooks/useAuth'
import { toast } from 'react-toastify'
import { getErrorMessage } from 'utils/helpers'
import { refetchCampaignLists } from 'slices/campaigns/reducer'
import Skeleton from 'react-loading-skeleton'
const CAMPAIGN_SEARCH_DEBOUNCE_MS = 500;
const CAMPAIGN_SEARCH_CACHE_MS = 5 * 60 * 1000; // 5 minutes

interface Campaign {
    id: number;
    name: string;
    uuid: string
}

const defaultCampaignValues = {
    name: '',
}

const AddToCampaignOffcanvas: React.FC<{ isActive: boolean, onClose: () => void, post: TrackedPost | null, onAddToCampaign: (campaign: Campaign, cb: () => void) => void }> = ({ isActive, onClose, post, onAddToCampaign }) => {
    const [searchQuery, setSearchQuery] = React.useState<string>('');
    const selectedAgencyId = useSelector((state: RootState) => state.auth.selectedAgencyId);
    const privileges = usePrivileges();

    const debounceTimeout = useRef<NodeJS.Timeout | null>(null);
    const [cachedCampaigns, setCachedCampaigns] = React.useState<{ [key: string]: { items: Campaign[], timestamp: number } }>({});

    const [selectedCampaign, setSelectedCampaign] = React.useState<null | Campaign>(null);
    const [processing, setProcessing] = React.useState(false);
    const [addNew, setAddNew] = React.useState(false);
    const [campaignsLoading, setCampaignsLoading] = React.useState(false);

    const dispatch: AppDispatch = useDispatch();

    const formFields = useFormik({
        // enableReinitialize : use this flag when initial values needs to be changed
        enableReinitialize: true,

        initialValues: defaultCampaignValues,
        validationSchema: Yup.object({
            name: Yup.string()
                .required("Campaign name field can't be empty")
                .max(100, "Maximum 100 characters are allowed"),
        }),
        onSubmit: async (campaignData) => {
            if (!selectedAgencyId) return;
            try {
                setProcessing(true);
                const response = await createOfflineCampaign(selectedAgencyId!, campaignData);
                if (!response.campaign?.uuid) throw new Error("Failed to create new campaign..");
                dispatch(refetchCampaignLists());
                setCachedCampaigns({});
                setSearchQuery('');
                fetchOfflineCampaigns('', true);
                addToCampaign(response.campaign);
            } catch (error) {
                toast.error(getErrorMessage(error, 'Failed to create new campaign'));
            } finally {
                setProcessing(false)
            }
        }
    });

    const fetchOfflineCampaigns = async (q: string = '', force: boolean = false) => {
        if (!selectedAgencyId) return;

        if (!force && cachedCampaigns[q] && (Date.now() - cachedCampaigns[q].timestamp < CAMPAIGN_SEARCH_CACHE_MS)) {
            // Use cached data
            return;
        }
        setCampaignsLoading(true)
        searchCampaigns(q, selectedAgencyId, { mode: 'offline' }).then((data) => {
            setCachedCampaigns((prev) => ({
                ...prev,
                [q]: {
                    items: data,
                    timestamp: Date.now()
                }
            }));
        }).finally(() => {
            setCampaignsLoading(false);
        });
    }

    useEffect(() => {
        formFields.resetForm();
    }, [addNew])

    useEffect(() => {
        if (!selectedAgencyId) return;
        fetchOfflineCampaigns();
    }, [selectedAgencyId]);

    const search = (q: string) => {
        if (debounceTimeout.current) {
            clearTimeout(debounceTimeout.current);
        }
        setCampaignsLoading(true);
        debounceTimeout.current = setTimeout(() => {
            fetchOfflineCampaigns(q).then(() => setCampaignsLoading(false));
        }, CAMPAIGN_SEARCH_DEBOUNCE_MS);
        setSearchQuery(q);
    }

    const addToCampaign = (campaign = selectedCampaign) => {
        if (campaign) {
            setProcessing(true);
            onAddToCampaign(campaign, () => {
                setProcessing(false);
                onClose();
            });
        }
    }

    useEffect(() => {
        if (isActive && post) {
            setSelectedCampaign(null);
            setSearchQuery('');
        }
    }, [isActive, post])

    const onCreate = async () => {
        formFields.submitForm();
    }


    return (
        <Offcanvas
            isOpen={isActive}
            direction="end"
            className="campaign-payments-offcanvas"
            toggle={onClose}
        >
            <CloseButton onClose={onClose} />
            <OffcanvasBody className="m-0 p-0 position-relative">
                <div className="payments-offcanvas-bg"></div>
                <div className="payments-offcanvas mt-2 py-4 px-4">
                    <div className="payments-offcanvas-header">
                        <h2>Add To Campaign</h2>
                        <span>Campaigns help you group related content so you can see performance, progress, and ROI in one place.</span>
                    </div>
                </div>
                {
                    post &&
                    <div className='px-4'>
                        {
                            addNew ? (
                                <div className='d-flex align-items-center justify-content-start gap-2'>
                                    <button className='unset-all text-primary mt-1' onClick={() => setAddNew(false)}><i className='bx bx-left-arrow-alt fs-3'></i></button>
                                    <span className='fw-bold m-0'>Create New Campaign</span>
                                </div>
                            ) : (
                                <div className="d-flex align-items-center-justify-content-between gap-3">
                                    <div className="campaign-layout-search-container position-relative me-2 w-100">
                                        <span>
                                            <svg width="16" height="17" viewBox="0 0 16 17" fill="none" xmlns="http://www.w3.org/2000/svg">
                                                <path d="M7.66732 14.0002C11.1651 14.0002 14.0007 11.1646 14.0007 7.66683C14.0007 4.16903 11.1651 1.3335 7.66732 1.3335C4.16951 1.3335 1.33398 4.16903 1.33398 7.66683C1.33398 11.1646 4.16951 14.0002 7.66732 14.0002Z" fill="#BBD8ED" stroke="#0069AD" strokeLinecap="round" strokeLinejoin="round" />
                                                <path d="M15 16L12 13" stroke="#0069AD" strokeLinecap="round" strokeLinejoin="round" />
                                            </svg>
                                        </span>
                                        <input type="text" placeholder='Search...' value={searchQuery} onChange={(e) => search(e.target.value)} className="campaign-layout-search-input w-100" />
                                        {
                                            searchQuery && (
                                                <span className="search-clear-icon" onClick={() => {
                                                    search('');
                                                }}>
                                                    <i className='bx bx-x' />
                                                </span>
                                            )
                                        }
                                    </div>
                                    <button className='theme-btn btn-gradient w-25' onClick={() => setAddNew(true)}>+ Create</button>
                                </div>
                            )
                        }
                        {
                            addNew ? (
                                <div>
                                    <FormGroup row className='py-4 m-0'>
                                        <Col sm={12} className='my-2'>
                                            <div className="d-flex gap-3">
                                                <div className="w-40px">
                                                    <span className="icon-circle me-3">
                                                        <CampaignIcon />
                                                    </span>
                                                </div>
                                                <div className="d-flex flex-column gap-2">
                                                    <Label htmlFor='name' className='form-label input-label mb-0'>
                                                        Campaign Name
                                                        <span className="text-danger"> *</span>
                                                    </Label>
                                                    <FieldInstruction instruction={FIELDS_INSTRUCTIONS.CAMPAIGN.NAME} />
                                                </div>
                                            </div>
                                        </Col>
                                        <Col sm={12} className='my-2'>
                                            <Input
                                                type='text'
                                                name='name'
                                                className='form-control'
                                                onChange={formFields.handleChange}
                                                onBlur={formFields.handleBlur}
                                                value={formFields.values.name || ""}
                                                invalid={formFields.touched.name && formFields.errors.name ? true : false}
                                                placeholder='Campaign name' />
                                            {
                                                formFields.touched.name && formFields.errors.name &&
                                                <FormFeedback type="invalid">
                                                    {formFields.errors.name}
                                                </FormFeedback>
                                            }
                                        </Col>
                                    </FormGroup>
                                </div>
                            ) : (
                                <div>
                                    <div className='mt-3 text-muted'>Recent Campaigns</div>
                                    <div
                                        className="list-group list-group-flush border-top border-bottom mt-2"
                                        style={{ maxHeight: '75vh', overflowY: 'auto' }}
                                    >
                                        {
                                            !campaignsLoading && (cachedCampaigns[searchQuery]?.items || []).map((campaign) => (
                                                <label onClick={() => setSelectedCampaign(campaign)} key={campaign.id} className="list-group-item d-flex align-items-center gap-3 py-3">
                                                    <input className="form-check-input mt-0" type="radio" name="campaign" />
                                                    <span>{campaign.name}</span>
                                                </label>
                                            ))
                                        }
                                        {
                                            campaignsLoading &&
                                            Array.from({ length: 4 }).map((_, i) => (
                                                <div key={i} className='d-flex align-items-center gap-3 py-3'>
                                                    <Skeleton height={20} width={10} circle={true} />
                                                    <Skeleton height={20} width={400} />
                                                </div>
                                            ))
                                        }
                                    </div>

                                    {
                                        (cachedCampaigns[searchQuery]?.items || []).length === 0 &&
                                        <div className="d-flex w-100 justify-content-center align-items-center no-campaigns mt-4">
                                            <h3 className="empty-msg">No campaigns found!</h3>
                                        </div>
                                    }
                                </div>
                            )
                        }
                    </div>
                }
                <div className='d-flex align-items-center justify-content-end gap-3 p-4 position-absolute bottom-0 w-100 bg-white border-top-1' >
                    <button className='theme-btn btn-gradient-outline' onClick={onClose}>Cancel</button>
                    {
                        addNew ? (
                            <button className='theme-btn btn-gradient' onClick={onCreate}>Create Campaign</button>
                        ) : (
                            <button className='theme-btn btn-gradient' onClick={() => addToCampaign(selectedCampaign)} disabled={processing || !selectedCampaign}>{processing ? 'Adding...' : 'Add To Campaign'}</button>
                        )
                    }
                </div>
            </OffcanvasBody>
        </Offcanvas>

    )
}

export default AddToCampaignOffcanvas
